import React, { useState } from "react";
import "./style.scss";
import PageContainer from "../PageContainer";
import {
  LayoutDashboard,
  AlertTriangle,
  Users,
  Settings,
  ClipboardList,
  Kanban,
} from "lucide-react";

// Calculate gradient based on percentage width
const getGradientForWidth = (
  percentage: number,
  gradientType: "bar-low" | "bar-red"
): string => {
  const colors =
    gradientType === "bar-low"
      ? ["red", "#fbbf24", "#22c55e"]
      : ["#22c55e", "#fbbf24", "red"];

  if (percentage <= 50) {
    // Gradient from first color to second color
    return `linear-gradient(to right, ${colors[0]} 5%, ${colors[1]} 100%)`;
  } else {
    // Gradient from first color through second to third
    return `linear-gradient(to right, ${colors[0]} 5%, ${colors[1]} 70%, ${colors[2]} 100%)`;
  }
};
const TABS = [
  {
    id: "control-risk",
    label: "Control Risk Rating",
    icon: <LayoutDashboard />,
  },
  { id: "audit-gaps", label: "Audit Gaps", icon: <AlertTriangle /> },
  { id: "issue-aging", label: "Issue Ageing", icon: <Users /> },
  { id: "evidence", label: "Evidence Quality", icon: <Kanban /> },
  { id: "closure", label: "Closure Health", icon: <Settings /> },
  { id: "actions", label: "Action Items", icon: <ClipboardList /> },
];

const AuditAnalytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState("control-risk");

  return (
    <div className="audit-root">
      <PageContainer>
        <div className="title">Audit Analytics</div>

        <div className="container-analytics">
          {/* Tabs */}
          <div className="audit-switcher">
            {/* ACTIVE (always) */}
            <div className="audit-box active">
              <span>Audit: AUD-121125-004</span>

              <div className="audit-selected">
                <span className="check-icon">✔</span>
                <span className="selected-text">Selected</span>
              </div>
            </div>

            {/* INACTIVE */}
            <div className="audit-box">Audit: AUD-121125-005</div>

            <div className="audit-box">Audit: AUD-121125-006</div>

            <div className="audit-box">Audit: AUD-121125-007</div>
          </div>

          <div className="tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon && <span>{tab.icon}</span>}
                {tab.label}
              </button>
            ))}
          </div>

          {/* =========================================================
                       TAB 1 — CONTROL RISK RATING
        ========================================================== */}

          {activeTab === "control-risk" && (
            <div className="tab-content">
              <div className="grid-2">
                {/* Card 1 */}
                <div className="card-analytics">
                  <div className="card-title-analytics">
                    IT Service Management
                    <div className="status-badge badge-partial">
                      Partially Effective
                    </div>
                  </div>

                  <div className="risk-matrix">
                    <div className="risk-box red">
                      <div className="risk-label">Non-Compliance</div>
                      <div className="risk-value">35</div>
                    </div>
                    <div className="risk-box teal">
                      <div className="risk-label">Compliance</div>
                      <div className="risk-value">85</div>
                    </div>
                  </div>

                  <div className="bar-item">
                    <div className="bar-label">
                      <span>Compliance Rating</span>
                      <span>
                        70%<span className="teal">(↑10%)</span>
                      </span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          width: "70%",
                          background: getGradientForWidth(48, "bar-low"),
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Card 2 */}
                <div className="card-analytics">
                  <div className="card-title-analytics">
                    Access Control
                    <div className="status-badge badge-partial">
                      Partially Effective
                    </div>
                  </div>

                  <div className="risk-matrix">
                    <div className="risk-box red">
                      <div className="risk-label">Non-Compliance</div>
                      <div className="risk-value">28</div>
                    </div>
                    <div className="risk-box teal">
                      <div className="risk-label">Compliance</div>
                      <div className="risk-value">62</div>
                    </div>
                  </div>

                  <div className="bar-item">
                    <div className="bar-label">
                      <span>Compliance Rating</span>
                      <span>
                        68%<span className="red">(↓8%)</span>
                      </span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          width: "68%",
                          background: getGradientForWidth(48, "bar-low"),
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Card 3 */}
                <div className="card-analytics">
                  <div className="card-title-analytics">
                    Data & Infrastructure Security
                    <div className="status-badge badge-effective">
                      Effective
                    </div>
                  </div>

                  <div className="risk-matrix">
                    <div className="risk-box red">
                      <div className="risk-label">Non-Compliance</div>
                      <div className="risk-value">5</div>
                    </div>
                    <div className="risk-box teal">
                      <div className="risk-label">Compliance</div>
                      <div className="risk-value">45</div>
                    </div>
                  </div>

                  <div className="bar-item">
                    <div className="bar-label">
                      <span>Compliance Rating</span>
                      <span>
                        90%<span className="teal">(↑23%)</span>
                      </span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          width: "90%",
                          background: getGradientForWidth(90, "bar-low"),
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Card 4 */}
                <div className="card-analytics">
                  <div className="card-title-analytics">
                    Cyber Security Operations
                    <div className="status-badge badge-not-effective">
                      Not Effective
                    </div>
                  </div>

                  <div className="risk-matrix">
                    <div className="risk-box red">
                      <div className="risk-label">Non-Compliance</div>
                      <div className="risk-value">78</div>
                    </div>
                    <div className="risk-box teal">
                      <div className="risk-label">Compliance</div>
                      <div className="risk-value">42</div>
                    </div>
                  </div>

                  <div className="bar-item">
                    <div className="bar-label">
                      <span>Compliance Rating</span>
                      <span>
                        35%<span className="red">(↓20%)</span>
                      </span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          width: "35%",
                          background: getGradientForWidth(3, "bar-low"),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* =========================================================
                       TAB 2 — AUDIT GAPS
        ========================================================== */}
          {activeTab === "audit-gaps" && (
            <div className="tab-content">
              <div className="grid-2">
                {/* Left card */}
                <div className="card-analytics">
                  <div className="card-title-analytics">Gaps by Severity</div>

                  {/* Item 1 */}
                  <div className="bar-item">
                    <div className="bar-label">
                      <span>AUD-121125-004</span>
                      <span>23 gaps</span>
                    </div>

                    <div className="bar-track">
                      <div
                        className="bar-fill bar-high"
                        style={{ width: "13%" }}
                      >
                        3H
                      </div>
                      <div
                        className="bar-fill bar-medium"
                        style={{ width: "35%" }}
                      >
                        8M
                      </div>
                      <div
                        className="bar-fill bar-low"
                        style={{ width: "52%" }}
                      >
                        12L
                      </div>
                    </div>
                  </div>

                  {/* Item 2 */}
                  <div className="bar-item">
                    <div className="bar-label">
                      <span>AUD-121125-005</span>
                      <span>14 gaps</span>
                    </div>

                    <div className="bar-track">
                      <div
                        className="bar-fill bar-high"
                        style={{ width: "7%" }}
                      >
                        1H
                      </div>
                      <div
                        className="bar-fill bar-medium"
                        style={{ width: "36%" }}
                      >
                        5M
                      </div>
                      <div
                        className="bar-fill bar-low"
                        style={{ width: "57%" }}
                      >
                        8L
                      </div>
                    </div>
                  </div>

                  {/* Item 3 */}
                  <div className="bar-item">
                    <div className="bar-label">
                      <span>AUD-121125-006</span>
                      <span>9 gaps</span>
                    </div>

                    <div className="bar-track">
                      <div
                        className="bar-fill bar-medium"
                        style={{ width: "33%" }}
                      >
                        3M
                      </div>
                      <div
                        className="bar-fill bar-low"
                        style={{ width: "67%" }}
                      >
                        6L
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right card */}
                <div className="card-analytics">
                  <div className="card-title-analytics">
                    Gap Type Distribution
                  </div>

                  <div className="bar-item">
                    <div className="bar-label">
                      <span>Process Gaps</span>
                      <span>64%</span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill bar-teal"
                        style={{ width: "64%" }}
                      />
                    </div>
                  </div>

                  <div className="bar-item">
                    <div className="bar-label">
                      <span>Technology Gaps</span>
                      <span>22%</span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill bar-teal"
                        style={{ width: "22%" }}
                      />
                    </div>
                  </div>

                  <div className="bar-item">
                    <div className="bar-label">
                      <span>Documentation</span>
                      <span>10%</span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill bar-teal"
                        style={{ width: "10%" }}
                      />
                    </div>
                  </div>

                  <div className="bar-item">
                    <div className="bar-label">
                      <span>People/Training</span>
                      <span>4%</span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill bar-teal"
                        style={{ width: "4%" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* =========================================================
                       TAB 3 — ISSUE AGEING
        ========================================================== */}
          {activeTab === "issue-aging" && (
            <div className="tab-content">
              <div className="card-analytics">
                <div className="card-title-analytics">
                  Findings by Age Bucket
                </div>

                <div className="bar-item">
                  <div className="bar-label">
                    <span>Distribution</span>
                    <span>15 total open findings</span>
                  </div>

                  <div className="bar-track">
                    <div
                      className="bar-fill"
                      style={{ width: "27%", background: "#32B8C6" }}
                    >
                      4
                    </div>
                    <div
                      className="bar-fill"
                      style={{ width: "40%", background: "#F5A961" }}
                    >
                      6
                    </div>
                    <div
                      className="bar-fill"
                      style={{ width: "20%", background: "#E67E50" }}
                    >
                      3
                    </div>
                    <div
                      className="bar-fill"
                      style={{ width: "13%", background: "#C01530" }}
                    >
                      2
                    </div>
                  </div>
                </div>

                {/* Buckets */}
                <div className="aging-buckets">
                  <div className="bucket bucket-fresh">
                    <div className="bucket-count">4</div>
                    <div className="bucket-label">0–30 Days</div>
                    <div className="bucket-desc">Fresh</div>
                  </div>

                  <div className="bucket bucket-aging">
                    <div className="bucket-count">6</div>
                    <div className="bucket-label">31–60 Days</div>
                    <div className="bucket-desc">Aging</div>
                  </div>

                  <div className="bucket bucket-at-risk">
                    <div className="bucket-count">3</div>
                    <div className="bucket-label">61–90 Days</div>
                    <div className="bucket-desc">At Risk</div>
                  </div>

                  <div className="bucket bucket-overdue">
                    <div className="bucket-count">2</div>
                    <div className="bucket-label">91+ Days</div>
                    <div className="bucket-desc">⚠ Overdue</div>
                  </div>
                </div>

                {/* Overdue section */}
                <div
                  style={{
                    marginTop: "25px",
                    paddingTop: "20px",
                    borderTop: "1px solid var(--color-border)",
                  }}
                >
                  <div style={{ fontSize: "12px", marginBottom: "15px" }}>
                    <strong>Overdue Findings (Requiring Escalation):</strong>
                  </div>

                  <div className="action-item">
                    <div>
                      <div className="action-title">
                        FND-002: 92 days outstanding
                      </div>
                      <div className="action-owner">
                        IT Team • 2 days overdue
                      </div>
                    </div>
                    <div className="action-priority priority-critical">
                      Escalate
                    </div>
                  </div>

                  <div className="action-item">
                    <div>
                      <div className="action-title">
                        FND-005: 95 days outstanding
                      </div>
                      <div className="action-owner">
                        HR Department • 5 days overdue
                      </div>
                    </div>
                    <div className="action-priority priority-critical">
                      Escalate
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* =========================================================
                       TAB 4 — EVIDENCE QUALITY
        ========================================================== */}
          {activeTab === "evidence" && (
            <div className="tab-content">
              <div className="grid-2">
                {/* Payment Processing */}
                <div className="card-analytics">
                  <div className="card-title-analytics">
                    Evidence Quality - Payment Processing
                  </div>

                  {/* Clean Center Gauge */}
                  <div className="gauge-container-center">
                    <div className="gauge-score">91%</div>
                    <div className="status-badge badge-effective gauge-status">
                      EXCELLENT
                    </div>
                  </div>

                  <div className="evidence-dimensions">
                    <div className="dimension-item">
                      <div className="dimension-name">Completeness</div>
                      <div className="dimension-bar">
                        <div
                          className="dimension-fill"
                          style={{ width: "95%" }}
                        />
                      </div>
                      <div className="dimension-score">95%</div>
                    </div>

                    <div className="dimension-item">
                      <div className="dimension-name">Relevance</div>
                      <div className="dimension-bar">
                        <div
                          className="dimension-fill"
                          style={{ width: "90%" }}
                        />
                      </div>
                      <div className="dimension-score">90%</div>
                    </div>

                    <div className="dimension-item">
                      <div className="dimension-name">Reliability</div>
                      <div className="dimension-bar">
                        <div
                          className="dimension-fill"
                          style={{ width: "88%" }}
                        />
                      </div>
                      <div className="dimension-score">88%</div>
                    </div>
                  </div>
                </div>

                {/* Access Controls */}
                <div className="card-analytics">
                  <div className="card-title-analytics">
                    Evidence Quality - Access Controls
                  </div>

                  {/* Clean Center Gauge */}
                  <div className="gauge-container-center">
                    <div className="gauge-score">81%</div>
                    <div className="status-badge badge-partial gauge-status">
                      Needs Review
                    </div>
                  </div>

                  <div className="evidence-dimensions">
                    <div className="dimension-item">
                      <div className="dimension-name">Completeness</div>
                      <div className="dimension-bar">
                        <div
                          className="dimension-fill"
                          style={{ width: "78%" }}
                        />
                      </div>
                      <div className="dimension-score">78%</div>
                    </div>

                    <div className="dimension-item">
                      <div className="dimension-name">Relevance</div>
                      <div className="dimension-bar">
                        <div
                          className="dimension-fill"
                          style={{ width: "82%" }}
                        />
                      </div>
                      <div className="dimension-score">82%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* =========================================================
                       TAB 5 — CLOSURE HEALTH
        ========================================================== */}
          {activeTab === "closure" && (
            <div className="tab-content">
              <div className="grid-2">
                {/* Health Score */}
                <div className="card-analytics">
                  <div className="card-title-analytics">
                    Audit: AUD-121125-005 Health Score
                  </div>

                  {/* Clean Center Gauge */}
                  <div className="gauge-container-center">
                    <div className="gauge-score">88%</div>
                    <div className="gauge-subtext">On Track</div>
                  </div>

                  <div className="status-badge badge-effective center-badge">
                    GOOD
                  </div>
                </div>

                {/* Health Components */}
                <div className="card-analytics">
                  <div className="card-title-analytics">Health Components</div>

                  <div className="bar-item">
                    <div className="bar-label">
                      <span>Task Completion</span>
                      <span>85%</span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill bar-teal"
                        style={{ width: "85%" }}
                      />
                    </div>
                  </div>

                  <div className="bar-item">
                    <div className="bar-label">
                      <span>Controls Tested</span>
                      <span>92%</span>
                    </div>
                    <div className="bar-track">
                      <div
                        className="bar-fill bar-teal"
                        style={{ width: "92%" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Task Breakdown */}
                <div className="card-analytics">
                  <div className="card-title-analytics">
                    Task Completion Breakdown
                  </div>

                  <div className="breakdown-block">
                    <strong>Planning Phase:</strong> 100% (5/5 tasks)
                    <div className="breakdown-good">✓ Complete</div>
                  </div>

                  <div className="breakdown-block">
                    <strong>Fieldwork:</strong> 85% (17/20 tasks)
                    <div className="breakdown-warning">
                      ⏳ 3 control walkthroughs pending
                    </div>
                  </div>

                  <div className="breakdown-block">
                    <strong>Review & Reporting:</strong> 75% (3/4 tasks)
                    <div className="breakdown-warning">
                      ⏳ Final management review pending
                    </div>
                  </div>
                </div>

                {/* Control Testing */}
                <div className="card-analytics">
                  <div className="card-title-analytics">
                    Control Testing Status
                  </div>

                  <div className="bar-item">
                    <strong>Total Controls: 23</strong>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          width: "91%",
                          background: "var(--color-teal)",
                        }}
                      >
                        21 Validated
                      </div>
                    </div>
                  </div>

                  <div className="breakdown-block">
                    <strong>Validated:</strong> 21 (91%)
                    <div className="breakdown-good">✓ Successful</div>
                  </div>

                  <div className="breakdown-block">
                    <strong>With Exceptions:</strong> 2 (9%)
                    <div className="breakdown-warning">
                      ⏳ Remediation in progress
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* =========================================================
                       TAB 6 — ACTION ITEMS
        ========================================================== */}
          {activeTab === "actions" && (
            <div className="tab-content">
              <div className="card-analytics">
                <div className="card-title-analytics">
                  Action Items & Follow-up Status
                </div>

                {/* IN PROGRESS */}
                <div className="section-header">IN PROGRESS (4 items)</div>

                <div className="action-item">
                  <div>
                    <div className="action-title">
                      AI-001: Implement Dual Authorization
                    </div>
                    <div className="action-owner">
                      Finance Director • Due: Dec 20, 2024 • From AUD-121125-004
                    </div>
                  </div>
                  <div className="action-priority priority-critical">
                    Critical
                  </div>
                </div>

                <div className="action-item">
                  <div>
                    <div className="action-title">
                      AI-003: Refresh Training Documentation
                    </div>
                    <div className="action-owner">
                      HR Lead • Due: Jan 10, 2025 • From AUD-121125-005
                    </div>
                  </div>
                  <div className="action-priority priority-high">High</div>
                </div>

                {/* NOT STARTED */}
                <div className="section-header">NOT STARTED (3 items)</div>

                <div className="action-item">
                  <div>
                    <div className="action-title">
                      AI-002: Update Access Matrix
                    </div>
                    <div className="action-owner">
                      IT Manager • Due: Dec 25, 2024 • From AUD-121125-005
                    </div>
                  </div>
                  <div className="action-priority priority-high">High</div>
                </div>

                <div className="action-item">
                  <div>
                    <div className="action-title">
                      AI-007: Training Policy Review
                    </div>
                    <div className="action-owner">
                      Compliance Officer • Due: Jan 20, 2025 • From
                      AUD-121125-004
                    </div>
                  </div>
                  <div className="action-priority priority-medium">Medium</div>
                </div>

                {/* ON HOLD */}
                <div className="section-header">ON HOLD (1 item)</div>

                <div className="action-item">
                  <div>
                    <div className="action-title">
                      AI-004: System Configuration Review
                    </div>
                    <div className="action-owner">
                      CIO • Due: Jan 15, 2025 • From AUD-121125-006
                    </div>
                  </div>
                  <div className="action-priority priority-medium">Medium</div>
                </div>

                {/* COMPLETED */}
                <div className="section-header">COMPLETED (2 items)</div>

                <div className="action-item" style={{ opacity: 0.7 }}>
                  <div>
                    <div className="action-title">
                      AI-005: Monthly Reconciliation Schedule
                    </div>
                    <div className="action-owner">
                      Accounting Mgr • Completed: Dec 9, 2024 • From
                      AUD-121125-004
                    </div>
                  </div>
                  <div
                    className="action-priority"
                    style={{
                      fontSize: "11px",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      background: "rgba(32, 128, 128, 0.15)",
                      color: "var(--color-teal)",
                    }}
                  >
                    ✓ Done
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </PageContainer>
    </div>
  );
};

export default AuditAnalytics;
